import LocomotiveScroll from "locomotive-scroll";
import "locomotive-scroll/dist/locomotive-scroll.css";

const PARALLAX_SELECTOR = ".player-deco";

/** スクロール量に対する選手ビジュアルの移動比 */
const PARALLAX_SPEED = 0.03;

/** スクロールコールバックの実行間隔（ms） */
const PARALLAX_INTERVAL = 10;

/** スマホ判定（CSSの $w-sp と対応） */
const SP_QUERY = "(max-width: 765px)";

/** スマホ時のページ内リンクの上余白（ターゲットを画面上端からこの分下げる） */
const SP_SCROLL_OFFSET_REM = 6;

/** スマホでもオフセットを付けないアンカー */
const NO_OFFSET_HASHES = new Set(["#schedule", "#news"]);

const throttle = (fn, limit) => {
  let lastCall = 0;

  return (...args) => {
    const now = Date.now();
    if (now - lastCall < limit) return;

    lastCall = now;
    fn(...args);
  };
};

/** rem → px（ルートの font-size に追従） */
const remToPx = (rem) => rem * parseFloat(getComputedStyle(document.documentElement).fontSize);

/**
 * ページ内スクロールのオフセット
 * スマホは上に 6rem の余白を残す（負値 = ターゲットより上で止める）
 * #schedule / #news はオフセットなし
 */
const getScrollOffset = (hash) => {
  if (NO_OFFSET_HASHES.has(hash)) return 0;
  return window.matchMedia(SP_QUERY).matches ? -remToPx(SP_SCROLL_OFFSET_REM) : 0;
};

/**
 * 画面内にある .player-deco だけをスクロール量に応じて動かす
 *
 * @returns {(scroll: number) => void} Locomotive Scroll のコールバックに渡すハンドラ
 */
const createParallaxHandler = () => {
  const elements = [...document.querySelectorAll(PARALLAX_SELECTOR)];
  if (!elements.length) return () => {};

  // 画面内に入った位置を基準にずらすため、要素ごとに状態を持たせる
  const states = new Map(elements.map((element) => [element, { isInView: false, baseScrollY: null }]));

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const state = states.get(entry.target);
        if (!state) return;

        state.isInView = entry.isIntersecting;

        // 画面外では基準位置とtransformを捨て、再入場時に基準を取り直す
        if (!entry.isIntersecting) {
          state.baseScrollY = null;
          entry.target.style.transform = "";
        }
      });
    },
    {
      root: null,
      rootMargin: "20% 0px",
      threshold: 0,
    },
  );

  elements.forEach((element) => observer.observe(element));

  return throttle((scroll) => {
    states.forEach((state, element) => {
      if (!state.isInView) return;
      if (state.baseScrollY === null) state.baseScrollY = scroll;

      const distance = (scroll - state.baseScrollY) * PARALLAX_SPEED;
      element.style.transform = `translate3d(0, ${distance}px, 0)`;
    });
  }, PARALLAX_INTERVAL);
};

/** Locomotive Scroll v5（内部 Lenis）によるスムーススクロールとパララックス */
export const initSmoothScroll = () => {
  const updateParallax = createParallaxHandler();

  const locomotiveScroll = new LocomotiveScroll({
    lenisOptions: {
      smoothWheel: true,
      smoothTouch: true,
    },
    scrollCallback: ({ scroll }) => updateParallax(scroll),
  });

  const scrollToHash = (hash, { immediate = false } = {}) => {
    if (!hash || hash === "#") return;

    const target = document.querySelector(hash);
    if (!target) return;

    locomotiveScroll.scrollTo(target, {
      offset: getScrollOffset(hash),
      duration: immediate ? 0 : 1.2,
      immediate,
    });
  };

  // ページ内リンクはネイティブジャンプさせず Locomotive Scroll で移動する
  document.addEventListener("click", (e) => {
    const anchor = e.target.closest('a[href^="#"]');
    if (!anchor) return;

    const href = anchor.getAttribute("href");
    if (!href || href === "#" || !document.querySelector(href)) return;

    e.preventDefault();
    history.pushState(null, "", href);
    scrollToHash(href);
  });

  window.addEventListener("hashchange", () => scrollToHash(window.location.hash));

  // 別ページから #付きで来た場合は、レイアウト確定後に移動する（トップへ戻さない）
  if (window.location.hash) {
    requestAnimationFrame(() => scrollToHash(window.location.hash));
  }

  // 画面幅の変化で高さが変わるため、スクロール量を再計算させる
  window.addEventListener("resize", () => {
    requestAnimationFrame(() => locomotiveScroll.resize());
  });
};
